import React from "react";

import { GroupDTO } from "@/generated-client";
import { Localizer } from "@/main/react/Types";
import SimpleSelect from "@/main/react/component/SimpleSelect";
import DiscussionThoughtGroupFilter from "@/main/react/discussion/DiscussionThoughtGroupFilter";
import {
    DiscussionOpeningThoughtOrderingStyle,
    getDiscussionOpeningThoughtOrderingOptions,
} from "@/main/react/discussion/DiscussionOpeningThoughtOrdering";

/**
 * The group filter + ordering row for the discussion's thought list.
 *
 * It renders directly ABOVE that list — below the reply composer, not up with the section tabs where it
 * used to live. A control belongs next to what it changes: from above the composer the reader could not
 * see the list reorder when they used it, and sitting in the tab row it read as applying to the whole
 * section rather than to the list alone. See the 2026-07-30 amendment in
 * `designs/04-live/2026-05-15-discussion-opening-argument-sorting-and-lazy-group-controls-design.md`.
 *
 * The group filter self-hides when the discussion has FEWER THAN TWO attached groups (`DISCUSSION-82`,
 * widened from "no attached groups"): a filter over one option cannot change the outcome, and on a phone
 * it was spending a 99 CSS px row saying so. On most discussions this row is therefore just the ordering
 * select, which stands alone fine.
 */
export default function DiscussionThoughtListControls(props: {
    localizer: Localizer;
    discussionGroups?: GroupDTO[];
    hasAnyDiscussionGroups?: boolean;
    selectedGroupPublicIds?: string[];
    onSelectedGroupPublicIdsChange: (ids: string[]) => void;
    openingArgumentOrdering?: DiscussionOpeningThoughtOrderingStyle;
    onOpeningArgumentOrderingChange: (value: DiscussionOpeningThoughtOrderingStyle) => void;
    isGroupsLoading?: boolean;
    onEnsureDiscussionGroupsLoaded?: () => void | Promise<void>;
}) {
    const {
        localizer,
        discussionGroups = [],
        hasAnyDiscussionGroups = false,
        selectedGroupPublicIds = [],
        onSelectedGroupPublicIdsChange,
        openingArgumentOrdering = "best",
        onOpeningArgumentOrderingChange,
        isGroupsLoading = false,
        onEnsureDiscussionGroupsLoaded,
    } = props;

    const orderingOptions = React.useMemo(() => getDiscussionOpeningThoughtOrderingOptions(localizer), [localizer]);

    return (
        <div
            className="flex w-full flex-wrap items-center justify-end gap-2"
            data-playwright-id="discussion-opening-thought-controls"
        >
            <DiscussionThoughtGroupFilter
                groups={discussionGroups}
                localizer={localizer}
                selectedGroupPublicIds={selectedGroupPublicIds}
                onSelectedGroupPublicIdsChange={onSelectedGroupPublicIdsChange}
                hasAvailableGroups={hasAnyDiscussionGroups}
                isLoading={isGroupsLoading}
                onOpenRequested={onEnsureDiscussionGroupsLoaded}
            />

            <SimpleSelect
                value={openingArgumentOrdering}
                onValueChange={(value) => onOpeningArgumentOrderingChange(value as DiscussionOpeningThoughtOrderingStyle)}
                options={orderingOptions}
                size="sm"
                ariaLabel={localizer("discussion.thoughts.ordering.aria")}
                dataPlaywrightId="discussion-opening-thought-ordering-select"
            />
        </div>
    );
}
